import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import isNil from "lodash/isNil";
import set from "lodash/set";
import PropTypes from "prop-types";
import React from "react";
import Icon from "../Icon";
import { connect } from "react-redux";
import ReactTooltip from "react-tooltip";
import * as actions from "../../modules/configure-wevo/actions";
import * as configureWevoConstants from "../../modules/configure-wevo/constants";
import * as tracker from "../../modules/tracker/actions";
import * as ConfigureProjectEvents from "../../modules/tracker/event-names/configure-wevo-events";

import Button from "../Button";
import AddAudienceCard from "./AddAudienceCard";
import EditAudienceCard from "./EditAudienceCard";
import AddCustomAudience from "./AddCustomAudience";

import { audienceProps, AUDIENCES } from "./audienceProps";
import { styles } from "./audienceStyles";

class AddAudience extends React.PureComponent {
    static propTypes = {
        // calculated by mapStateToProps
        audienceDescription: PropTypes.string.isRequired,
        audiences: PropTypes.arrayOf(PropTypes.shape(audienceProps)).isRequired,
        isValid: PropTypes.bool.isRequired,

        // from mapDispatchToProps
        attachAudienceToWevo: PropTypes.func.isRequired,
        detachAudienceFromWevo: PropTypes.func.isRequired,
        toggleQuotaEditorForAudience: PropTypes.func.isRequired,
        trackEvent: PropTypes.func.isRequired,
        updateAudienceSegments: PropTypes.func.isRequired,
        updateAudienceDescription: PropTypes.func.isRequired,

        // from parent component
        isReadOnly: PropTypes.bool.isRequired,
        nextStepText: PropTypes.string.isRequired,
        toNextStep: PropTypes.func.isRequired,
        wevoId: PropTypes.number.isRequired
    };

    static isValidAudience(audience) {
        return (
            audience.segments
                .filter(s => s.isSelected)
                .map(s => s.percentage)
                .reduce((sum, p) => sum + p, 0) === 100
        );
    }

    componentWillMount() {
        this.props.trackEvent(
            ConfigureProjectEvents.DID_NAVIGATE_TO_SELECT_AUDIENCE
        );
    }

    static distributePercentageOverSelectedSegments(
        totalPercentage,
        allSegments,
        selectedSegments
    ) {
        const basePercentage = Math.floor(
            totalPercentage / selectedSegments.length
        );
        const remainder = totalPercentage % selectedSegments.length;
        return allSegments.map(segment => {
            const selectedIndex = selectedSegments.findIndex(
                selected => selected.id === segment.id
            );
            const isSelected = selectedIndex >= 0;

            // if the number of selected segments does not evenly divide into `totalPercentage`, then we distribute the
            // extra percentage points among the first `remainder` selected segments.
            const percentage = isSelected
                ? selectedIndex < remainder
                    ? basePercentage + 1
                    : basePercentage
                : 0;

            return {
                id: segment.id,
                percentage,
                isSelected
            };
        });
    }

    changeAudienceDescription(description) {
        if (this.props.isReadOnly) {
            return Promise.resolve();
        }
        return this.props.updateAudienceDescription(
            this.props.wevoId,
            description
        );
    }

    changeSegmentSelection(aud, segments) {
        if (this.props.isReadOnly) {
            return Promise.resolve();
        }

        if (segments.length === 0) {
            // we don't allow an attached audience with 0 segments so I'll just keep the current state for now
            return Promise.resolve();
        }

        const requestSegments = AddAudience.distributePercentageOverSelectedSegments(
            100,
            aud.segments,
            segments
        );

        return this.props.updateAudienceSegments(
            this.props.wevoId,
            aud.id,
            requestSegments
        );
    }

    changeSegmentQuota(aud, segment, value) {
        if (this.props.isReadOnly) {
            return Promise.resolve();
        }

        const selectedSegments = aud.segments.filter(s => s.isSelected);
        if (selectedSegments.length === 2) {
            // When the user adjusts a segment's quota and there are only 2 segments selected, we automatically adjust
            // the other segment's quota to keep the total at 100.
            const remaining = 100 - value;
            const unchangedSegment = selectedSegments.find(
                s => s.id !== segment.id
            );

            return this.props.updateAudienceSegments(
                this.props.wevoId,
                aud.id,
                [
                    {
                        id: segment.id,
                        percentage: value,
                        isSelected: true
                    },
                    {
                        id: unchangedSegment.id,
                        percentage: remaining,
                        isSelected: true
                    }
                ]
            );
        } else {
            return this.props.updateAudienceSegments(
                this.props.wevoId,
                aud.id,
                [
                    {
                        id: segment.id,
                        percentage: value,
                        isSelected: true
                    }
                ]
            );
        }
    }

    addAudience(aud) {
        if (this.props.isReadOnly) {
            return Promise.resolve();
        }

        return this.props.attachAudienceToWevo(this.props.wevoId, aud.id);
    }

    removeAudience(aud) {
        if (this.props.isReadOnly) {
            return Promise.resolve();
        }

        return this.props.detachAudienceFromWevo(this.props.wevoId, aud.id);
    }

    toggleEditQuotas(aud) {
        return this.props.toggleQuotaEditorForAudience(
            this.props.wevoId,
            aud.id
        );
    }

    render() {
        const nextStepsButtonProps = this.props.isValid
            ? {
                  disabled: false,
                  onClick: () => this.props.toNextStep(this.props.wevoId),
                  color: "primary"
              }
            : {
                  disabled: true,
                  onClick: null,
                  color: "disabled"
              };

        return (
            <div className={styles.chapter}>
                <div className={styles.chapterHeader}>
                    <h2 className={styles.chapterTitle}>Add Audience</h2>
                    <p className={styles.chapterSubtitle}>
                        Choose your target audience demographics for this test.
                        <Icon
                            icon="info-circle"
                            className={styles.infoTooltipIcon}
                            data-tip
                            data-for="add-audience-tooltip"
                        />
                    </p>
                    <ReactTooltip
                        id="add-audience-tooltip"
                        place="bottom"
                        border={true}
                        type="light"
                        effect="solid"
                    >
                        <div className={styles.infoTooltipContent}>
                            <p>
                                Audience will be within +/-5% of your targeted
                                demographic.
                            </p>
                            <p>
                                If your target audience group is too small, we
                                will contact you.
                            </p>
                        </div>
                    </ReactTooltip>
                </div>
                <div className={styles.audienceCards}>
                    {this.props.audiences.map(
                        aud =>
                            aud.isSelected ? (
                                <EditAudienceCard
                                    {...aud}
                                    key={aud.id}
                                    onCloseEditQuotas={() =>
                                        this.toggleEditQuotas(aud)
                                    }
                                    onOpenEditQuotas={() =>
                                        this.toggleEditQuotas(aud)
                                    }
                                    onRemove={() => this.removeAudience(aud)}
                                    onSegmentChange={segments =>
                                        this.changeSegmentSelection(
                                            aud,
                                            segments
                                        )
                                    }
                                    onSegmentQuotaChange={(segment, value) =>
                                        this.changeSegmentQuota(
                                            aud,
                                            segment,
                                            value
                                        )
                                    }
                                />
                            ) : (
                                !this.props.isReadOnly && (
                                    <AddAudienceCard
                                        {...aud}
                                        key={aud.id}
                                        onClick={() => this.addAudience(aud)}
                                    />
                                )
                            )
                    )}
                    {!(
                        this.props.isReadOnly && !this.props.audienceDescription
                    ) && (
                        <AddCustomAudience
                            isReadOnly={this.props.isReadOnly}
                            audienceDescription={this.props.audienceDescription}
                            onAudienceDescriptionChange={description =>
                                this.changeAudienceDescription(description)
                            }
                        />
                    )}
                </div>
                <div className={styles.nextStepWrapper}>
                    <Button {...nextStepsButtonProps}>
                        {this.props.nextStepText}
                    </Button>
                </div>
            </div>
        );
    }
}

export function mapStateToProps(state, ownProps) {
    const wevoAudiences = ownProps.wevo.audiences;

    const audienceDescription = ownProps.wevo.audienceDescription || "";

    const allSegmentsSumTo100 = wevoAudiences.every(
        AddAudience.isValidAudience
    );

    const isValid = !isNil(wevoAudiences) && allSegmentsSumTo100;

    const attachedAudiences = wevoAudiences.reduce(
        (obj, aud) => set(obj, aud.id, aud),
        {}
    );

    // Figure out which audiences have their AudienceSegmentQuotaEditor open
    const openQuotas = get(
        state.configureWevo,
        [
            configureWevoConstants.SELECT_AUDIENCE_REDUCER_KEY,
            ownProps.wevoId,
            "openQuotas"
        ],
        new Set()
    );

    // Figure out which audiences have a request in flight. We limit
    // interactions with these audiences until we get a response
    const requestInFlight = get(
        state.configureWevo,
        [
            configureWevoConstants.SELECT_AUDIENCE_REDUCER_KEY,
            ownProps.wevoId,
            "inFlightRequests"
        ],
        new Set()
    );

    const audiences = AUDIENCES.map(aud => {
        // make a copy to ensure we don't modify the static object
        aud = cloneDeep(aud);
        const audienceState = attachedAudiences[aud.id];

        aud.isSelected = !!audienceState;
        aud.quotasOpen = openQuotas.has(aud.id);
        aud.isLoading = requestInFlight.has(aud.id);
        aud.isReadOnly = ownProps.isReadOnly;

        const attachedSegments = audienceState
            ? audienceState.segments.reduce(
                  (obj, seg) => set(obj, seg.segment.id, seg),
                  {}
              )
            : {};
        for (const segment of aud.segments) {
            const segmentState = attachedSegments[segment.id];
            segment.isSelected = segmentState ? segmentState.isSelected : false;
            segment.percentage = segmentState ? segmentState.percentage : 0;
        }

        return aud;
    });

    return {
        audienceDescription,
        audiences,
        isValid
    };
}

function mapDispatchToProps(dispatch) {
    return {
        attachAudienceToWevo: (wevoId, audienceId) =>
            dispatch(actions.attachAudienceToWevo(wevoId, audienceId)),
        detachAudienceFromWevo: (wevoId, audienceId) =>
            dispatch(actions.detachAudienceFromWevo(wevoId, audienceId)),
        toggleQuotaEditorForAudience: (wevoId, audienceId) =>
            dispatch(actions.toggleQuotaEditorForAudience(wevoId, audienceId)),
        trackEvent: (name, props) => dispatch(tracker.trackEvent(name, props)),
        updateAudienceSegments: (wevoId, audienceId, segments) =>
            dispatch(
                actions.updateAudienceSegments(wevoId, audienceId, segments)
            ),
        updateAudienceDescription: (wevoId, audienceDescription) =>
            dispatch(
                actions.updateWevo(wevoId, {
                    audienceDescription: audienceDescription
                })
            )
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(AddAudience);



// WEBPACK FOOTER //
// ./src/components/CreateTest/AddAudience.js